import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Topbar } from "./Topbar";
import { Footer } from "./Footer";
import { LogoReferences } from "./LogoReferences";
import { Hero } from "./Hero";
import { Section } from "./Section";
import { RevealOnScroll } from "./RevealOnScroll";
import { RelatedServices } from "./RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

const LOGO_MODULES = import.meta.glob(
  "../../assets/logos/picazzoo.{png,svg,webp,jpg}",
  { eager: true, import: "default" },
) as Record<string, string>;
const PICAZZOO_LOGO: string | undefined = Object.values(LOGO_MODULES)[0];

export type PicazzooContent = {
  lang: "de" | "en";
  eyebrow: string;
  headline: ReactNode;
  subline: string;
  metaStrip: string[];
  breadcrumbs: { label: string; to?: string }[];

  standpunktKicker: string;
  standpunktHeadline: ReactNode;
  standpunktParagraphs: ReactNode[];

  pillarsKicker: string;
  pillarsHeadline: ReactNode;
  pillars: { num: string; name: string; tagline: string; detail: string; icon: ReactNode }[];

  uspsKicker: string;
  uspsHeadline: ReactNode;
  usps: { num: string; name: string; detail: string }[];

  capsKicker: string;
  capsHeadline: ReactNode;
  caps: { name: string; detail: string }[];

  valueKicker: string;
  valueHeadline: ReactNode;
  values: { strong: string; rest: string }[];

  audienceKicker: string;
  audienceHeadline: ReactNode;
  audience: string[];

  crossKicker: string;
  crossHeadline: ReactNode;
  crossIntro: string;
  crossItems: { label: string; before: string; linkLabel: string; to: string; after: string }[];

  calvariusKicker: string;
  calvariusHeadline: ReactNode;
  calvariusBody: string;

  ctaKicker: string;
  ctaHeadline: ReactNode;

  cardA: { eyebrow: string; headline: string; body: string; button: string };
  cardB: { eyebrow: string; headline: string; body: string; button: string; to: string };
};

function Wordmark({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const cls =
    size === "lg"
      ? "text-5xl md:text-6xl"
      : size === "sm"
      ? "text-2xl"
      : "text-3xl md:text-4xl";
  return (
    <span className="inline-flex items-baseline gap-3">
      <span className={`font-display font-semibold tracking-tight lowercase ${cls}`}>picazzoo</span>
      <span className="font-display italic text-ink-3 text-base md:text-lg">AI-Powered Visual Creation</span>
    </span>
  );
}

function ExternalIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="ml-1 inline-block"
    >
      <path d="M7 17L17 7" />
      <path d="M7 7h10v10" />
    </svg>
  );
}

export function PicazzooPage(p: PicazzooContent) {
  const picazzooLogoLabel = p.lang === "en" ? "Logo: picazzoo — AI-Powered Visual Creation" : "Logo: picazzoo — KI-gestützte Bildproduktion";

  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow={p.eyebrow}
          headline={p.headline}
          lead={p.subline}
          metaStrip={p.metaStrip}
          breadcrumbs={p.breadcrumbs}
        />

        {/* Standpunkt */}
        <Section number="02" kicker={p.standpunktKicker} headline={p.standpunktHeadline}>
          <RevealOnScroll>
            <div className="mb-10">
              <Wordmark size="lg" />
            </div>
            <div className="prose max-w-2xl space-y-5 text-[17px] leading-relaxed text-ink-2">
              {p.standpunktParagraphs.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* Three core areas */}
        <Section variant="tinted" number="03" kicker={p.pillarsKicker} headline={p.pillarsHeadline}>
          <RevealOnScroll>
            {PICAZZOO_LOGO && (
              <div className="mb-8">
                <img src={PICAZZOO_LOGO} alt={picazzooLogoLabel} title={picazzooLogoLabel} width={200} height={40} loading="lazy" decoding="async" className="h-10 w-auto" />
              </div>
            )}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {p.pillars.map((pi) => (
                <div key={pi.num} className="flex flex-col rounded-lg border border-rule bg-white p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-cyan/10 text-cyan">
                      {pi.icon}
                    </span>
                    <span className="font-mono text-xs uppercase tracking-wider text-ink-4">{pi.num}</span>
                  </div>
                  <h3 className="font-display text-xl font-semibold text-ink">{pi.name}</h3>
                  <div className="mt-2 text-sm font-medium text-ink-2">{pi.tagline}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{pi.detail}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* USPs */}
        <Section number="04" kicker={p.uspsKicker} headline={p.uspsHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4">
              {p.usps.map((u) => (
                <div key={u.num} className="bg-white p-6">
                  <div className="font-mono text-xs uppercase tracking-wider text-ink-4">{u.num}</div>
                  <h3 className="mt-3 font-display text-lg font-semibold text-ink leading-snug">{u.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{u.detail}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* Capabilities */}
        <Section variant="tinted" number="05" kicker={p.capsKicker} headline={p.capsHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {p.caps.map((c, i) => (
                <div key={i} className="bg-white p-5">
                  <div className="font-display text-base font-semibold text-ink">{c.name}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{c.detail}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* Value */}
        <Section number="06" kicker={p.valueKicker} headline={p.valueHeadline}>
          <RevealOnScroll>
            <ul className="space-y-4">
              {p.values.map((it, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-5">
                  <span className="text-cyan" aria-hidden>●</span>
                  <div className="text-ink-2 leading-relaxed">
                    <strong className="font-semibold text-ink">{it.strong}</strong> {it.rest && <>— {it.rest}</>}
                  </div>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        {/* Audience */}
        <Section variant="tinted" number="07" kicker={p.audienceKicker} headline={p.audienceHeadline}>
          <RevealOnScroll>
            <ul className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {p.audience.map((a, i) => (
                <li key={i} className="flex items-baseline gap-4 bg-white px-5 py-4 text-ink-2">
                  <span className="text-cyan" aria-hidden>●</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <RelatedServices
          locale={p.lang}
          fromId="picazzoo"
          number="08"
          items={relatedBySource["picazzoo"]}
        />

        {/* Calvarius origin */}
        <Section variant="tinted" number="09" kicker={p.calvariusKicker} headline={p.calvariusHeadline}>
          <RevealOnScroll>
            <p className="max-w-2xl text-[17px] leading-relaxed text-ink-2">{p.calvariusBody}</p>
          </RevealOnScroll>
        </Section>

        <LogoReferences locale={p.lang} number="10" />

        {/* Dual CTA */}
        <Section variant="deep" number="11" kicker={p.ctaKicker} headline={p.ctaHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Card A — picazzoo.com (with prominent logo) */}
              <div className="rounded-lg border border-cyan/30 bg-white p-8 text-ink">
                {PICAZZOO_LOGO ? (
                  <img src={PICAZZOO_LOGO} alt={picazzooLogoLabel} title={picazzooLogoLabel} width={280} height={56} loading="lazy" decoding="async" className="mb-6 h-14 w-auto" />
                ) : (
                  <div className="mb-6">
                    <Wordmark size="md" />
                  </div>
                )}
                <p className="font-mono text-xs uppercase tracking-wider text-ink-4">{p.cardA.eyebrow}</p>
                <h3 className="mt-3 font-display text-2xl font-semibold leading-snug">{p.cardA.headline}</h3>
                <p className="mt-4 text-ink-3 leading-relaxed">{p.cardA.body}</p>
                <a
                  href="https://picazzoo.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-cyan px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-white transition-colors hover:bg-cyan/90"
                >
                  {p.cardA.button}
                  <ExternalIcon />
                </a>
              </div>

              {/* Card B — Mandate */}
              <div className="rounded-lg border border-white/15 bg-white/5 p-8 text-white">
                <p className="font-mono text-xs uppercase tracking-wider text-white/85">{p.cardB.eyebrow}</p>
                <h3 className="mt-3 font-display text-2xl font-semibold leading-snug">{p.cardB.headline}</h3>
                <p className="mt-4 leading-relaxed text-white/75">{p.cardB.body}</p>
                <Link
                  to={p.cardB.to}
                  className="mt-6 inline-flex items-center gap-2 rounded-md border border-white/30 px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-white transition-colors hover:bg-white/10"
                >
                  {p.cardB.button} →
                </Link>
              </div>
            </div>
          </RevealOnScroll>
        </Section>
      </main>
      <Footer />
    </>
  );
}
